import { REPORT_PAGE_NUMBERS } from '~/modules/tools/views/PlagiarismCheckerV4/report/reportStructure';
import {
  CitationAwareReportViewModel,
  CitationAwareScanResult,
  CitationStatus,
  HighlightAnnotation,
  HighlightSegment,
  HighlightSegmentKind,
  PlagiarismInputCitation,
  PlagiarismMatchType,
  PlagiarismVectorSource,
  PlagiarismWebSource,
  ReportMatchedSource,
  TextSpan,
} from '~/modules/tools/views/PlagiarismCheckerV4/report/types';
import {
  isPresentableVectorSource,
  isPresentableWebSource,
  resolveHighlightSegmentKind,
  sanitizeCitationAwareScanResult,
} from '~/modules/tools/views/PlagiarismCheckerV4/report/utils/citationAwareScan';
import {
  generateCitationAwareSummary,
  generateCitationAwareSummarySegments,
} from '~/modules/tools/views/PlagiarismCheckerV4/report/utils/generateSummary';

const roundPct = (value: number): number => {
  return Math.round(value * 10) / 10;
};

const getWordsPercentage = (
  words: number | undefined,
  totalWords: number | undefined,
): number => {
  if (!words || !totalWords) {
    return 0;
  }

  return roundPct((words / totalWords) * 100);
};

const spanKey = (span: TextSpan): string => {
  return `${span[0]}:${span[1]}`;
};

const spanOverlaps = (span: TextSpan, container: TextSpan): boolean => {
  const [offset, length] = span;
  const [containerOffset, containerLength] = container;

  return (
    offset >= containerOffset &&
    offset + length <= containerOffset + containerLength
  );
};

const getShortSourceName = (title: string): string => {
  const parts = title.split(/\s+[—–]\s+/);
  const lastPart = parts[parts.length - 1] || title;

  return lastPart.replace(/^The\s+/i, '').trim();
};

const findMatchedSourceForSpan = (
  span: TextSpan,
  sources: ReportMatchedSource[],
  result: CitationAwareScanResult,
): ReportMatchedSource | undefined => {
  const allSources = [...result.web_sources, ...result.vector_sources];

  for (let index = 0; index < allSources.length; index += 1) {
    const source = allSources[index];
    const sourceSpans = source.input_plagiarism || [];

    if (sourceSpans.some((sourceSpan) => spanOverlaps(span, sourceSpan))) {
      return sources.find(
        (item) =>
          item.title === source.title && item.type === source.source_type,
      );
    }
  }

  return undefined;
};

const findMatchTypeForSpan = (
  span: TextSpan,
  result: CitationAwareScanResult,
): PlagiarismMatchType | undefined => {
  if (
    result.input_exact_plagiarism.some((exactSpan) =>
      spanOverlaps(span, exactSpan),
    )
  ) {
    return 'exact';
  }

  if (
    result.input_minor_plagiarism.some((minorSpan) =>
      spanOverlaps(span, minorSpan),
    )
  ) {
    return 'minor';
  }

  return undefined;
};

const buildAnnotation = (
  span: TextSpan,
  kind: HighlightSegmentKind,
  result: CitationAwareScanResult,
  matchedSources: ReportMatchedSource[],
): HighlightAnnotation | undefined => {
  if (kind === 'plain' || kind === 'references') {
    return undefined;
  }

  const matchedSource = findMatchedSourceForSpan(span, matchedSources, result);
  const isCited =
    kind === 'properly_cited_quoted' || kind === 'cited_not_quoted';

  if (!matchedSource && !isCited) {
    return undefined;
  }

  return {
    source_index: matchedSource?.index,
    source_name: matchedSource
      ? getShortSourceName(matchedSource.title)
      : undefined,
    is_cited: isCited,
    match_type: isCited ? undefined : findMatchTypeForSpan(span, result),
  };
};

const buildHighlightSegments = (
  inputText: string,
  result: CitationAwareScanResult,
  matchedSources: ReportMatchedSource[],
): HighlightSegment[] => {
  if (!inputText) {
    return [];
  }

  // Bibliography is rendered on the References page.
  const bodyTextEnd = result.references_span
    ? result.references_span[0]
    : inputText.length;
  const bodyText = inputText.slice(0, bodyTextEnd);

  const clipSpanToBody = (span: TextSpan): TextSpan | null => {
    const [offset, length] = span;

    if (offset >= bodyTextEnd) {
      return null;
    }

    const clippedLength = Math.min(length, bodyTextEnd - offset);

    return clippedLength > 0 ? [offset, clippedLength] : null;
  };

  const highlightSpans = [
    ...result.input_plagiarism,
    ...result.input_citations.map((citation) => citation.span),
  ]
    .map(clipSpanToBody)
    .filter((span): span is TextSpan => span !== null)
    .filter(
      (span, index, spans) =>
        spans.findIndex((item) => spanKey(item) === spanKey(span)) === index,
    )
    .sort((a, b) => a[0] - b[0]);

  if (!highlightSpans.length) {
    return bodyText ? [{ text: bodyText, kind: 'plain' }] : [];
  }

  const segments: HighlightSegment[] = [];
  let cursor = 0;

  highlightSpans.forEach((span) => {
    const [offset, length] = span;

    if (offset < cursor) {
      // Overlapping spans — earlier segment already covers this range.
      return;
    }

    if (offset > cursor) {
      segments.push({
        text: bodyText.slice(cursor, offset),
        kind: 'plain',
      });
    }

    const kind = resolveHighlightSegmentKind({
      span,
      inputText,
      result,
    });
    segments.push({
      text: bodyText.slice(offset, offset + length),
      kind: kind === 'references' ? 'plain' : kind,
      annotation: buildAnnotation(span, kind, result, matchedSources),
    });

    cursor = offset + length;
  });

  if (cursor < bodyText.length) {
    segments.push({
      text: bodyText.slice(cursor),
      kind: 'plain',
    });
  }

  return segments.filter((segment) => segment.text.length > 0);
};

const isQuotedAndCredited = (
  sourceSpans: TextSpan[],
  result: CitationAwareScanResult,
): boolean => {
  const properlyCitedSpanKeys = result.input_citations
    .filter((citation) => citation.status === 'properly_cited')
    .map((citation) => spanKey(citation.span));

  return sourceSpans.some((span) =>
    properlyCitedSpanKeys.includes(spanKey(span)),
  );
};

export const deriveSourceMatchTypes = (source: {
  input_exact_plagiarism_pct?: number;
  input_minor_plagiarism_pct?: number;
  input_exact_plagiarism?: TextSpan[];
  input_minor_plagiarism?: TextSpan[];
}): PlagiarismMatchType[] => {
  const exactSpans = source.input_exact_plagiarism || [];
  const minorSpans = source.input_minor_plagiarism || [];
  const hasExact =
    (source.input_exact_plagiarism_pct || 0) > 0 || exactSpans.length > 0;
  const hasMinor =
    (source.input_minor_plagiarism_pct || 0) > 0 || minorSpans.length > 0;

  const types: PlagiarismMatchType[] = [];

  if (hasExact) {
    types.push('exact');
  }

  if (hasMinor) {
    types.push('minor');
  }

  return types;
};

const mapMatchedSources = (
  result: CitationAwareScanResult,
): ReportMatchedSource[] => {
  const sources: ReportMatchedSource[] = [];
  let index = 1;

  result.web_sources.filter(isPresentableWebSource).forEach((source) => {
    sources.push({
      index,
      type: 'web',
      title: source.title,
      url: source.url,
      snippet: source.snippet,
      authors: [],
      year: null,
      publisher: null,
      match_types: deriveSourceMatchTypes(source),
      similarity_pct: roundPct(source.input_plagiarism_pct),
      quoted_and_credited: isQuotedAndCredited(
        source.input_plagiarism || [],
        result,
      ),
    });
    index += 1;
  });

  result.vector_sources.filter(isPresentableVectorSource).forEach((source) => {
    sources.push({
      index,
      type: 'vector',
      title: source.title,
      url: source.oa_url || source.doi_url || null,
      snippet: null,
      authors: source.authors,
      year: source.year || null,
      publisher: source.publisher || null,
      match_types: deriveSourceMatchTypes(source),
      similarity_pct: roundPct(source.input_plagiarism_pct),
      quoted_and_credited: isQuotedAndCredited(
        source.input_plagiarism || [],
        result,
      ),
    });
    index += 1;
  });

  return sources;
};

const buildSourcesSummaryText = (result: CitationAwareScanResult): string => {
  const backendLabels: string[] = [];
  const presentableWebSources = result.web_sources.filter(
    isPresentableWebSource,
  );
  const presentableVectorSources = result.vector_sources.filter(
    isPresentableVectorSource,
  );

  if (presentableWebSources.length > 0) {
    backendLabels.push('Web search');
  }

  if (presentableVectorSources.length > 0) {
    backendLabels.push('Database');
  }

  const backendsText = backendLabels.length
    ? backendLabels.join(' + ')
    : 'No sources matched';

  return result.references_span
    ? `${backendsText}, references excluded`
    : backendsText;
};

const ENGINE_LABEL = 'Originality Engine V1';

export const mapCitationAwareScanResult = ({
  scanId,
  createdAt,
  inputText,
  result,
}: {
  scanId: string;
  createdAt: string;
  inputText: string;
  result: CitationAwareScanResult;
}): CitationAwareReportViewModel => {
  const sanitizedResult = sanitizeCitationAwareScanResult(result);
  const overallSimilarityPct = roundPct(sanitizedResult.input_plagiarism_pct);
  const properlyAttributedPct = getWordsPercentage(
    result.properly_cited_quoted_num_words,
    sanitizedResult.num_words_total,
  );
  const needsAttentionPct = roundPct(
    Math.max(overallSimilarityPct - properlyAttributedPct, 0),
  );
  const matchedSources = mapMatchedSources(sanitizedResult);
  const excludeReferences = Boolean(sanitizedResult.references_span);
  const referencesText = sanitizedResult.references_span
    ? inputText
        .slice(
          sanitizedResult.references_span[0],
          sanitizedResult.references_span[0] +
            sanitizedResult.references_span[1],
        )
        .trim() || null
    : null;

  return {
    scan_id: scanId,
    created_at: createdAt,
    input_text: inputText,
    overall_similarity_pct: overallSimilarityPct,
    needs_attention_pct: needsAttentionPct,
    properly_attributed_pct: properlyAttributedPct,
    exact_match_pct: roundPct(sanitizedResult.input_exact_plagiarism_pct),
    exact_match_words: sanitizedResult.input_exact_plagiarism_num_words,
    minor_match_pct: roundPct(sanitizedResult.input_minor_plagiarism_pct),
    minor_match_words: sanitizedResult.input_minor_plagiarism_num_words,
    properly_cited_quoted_pct: properlyAttributedPct,
    total_words: sanitizedResult.num_words_total,
    properly_cited_quoted_words:
      sanitizedResult.properly_cited_quoted_num_words,
    omitted_words: sanitizedResult.omitted_num_words,
    sources_count: matchedSources.length,
    contains_citations: sanitizedResult.contains_citations,
    exclude_references: excludeReferences,
    references_text: referencesText,
    document_pages: sanitizedResult.document_pages ?? null,
    engine_label: ENGINE_LABEL,
    sources_summary_text: buildSourcesSummaryText(sanitizedResult),
    summary_text: generateCitationAwareSummary({
      overall_similarity_pct: overallSimilarityPct,
      needs_attention_pct: needsAttentionPct,
      properly_attributed_pct: properlyAttributedPct,
      exclude_references: excludeReferences,
      highlighted_text_page_number: REPORT_PAGE_NUMBERS.HIGHLIGHTED_TEXT,
    }),
    summary_segments: generateCitationAwareSummarySegments({
      overall_similarity_pct: overallSimilarityPct,
      needs_attention_pct: needsAttentionPct,
      properly_attributed_pct: properlyAttributedPct,
      exclude_references: excludeReferences,
      highlighted_text_page_number: REPORT_PAGE_NUMBERS.HIGHLIGHTED_TEXT,
    }),
    matched_sources: matchedSources,
    highlight_segments: buildHighlightSegments(
      inputText,
      sanitizedResult,
      matchedSources,
    ),
  };
};

export const normalizeCitationStatus = (
  status: unknown,
): CitationStatus | null => {
  if (status === 'properly_cited' || status === 'properly_cited_quoted') {
    return status;
  }

  if (status === 'missing' || status === 'improper') {
    return status;
  }

  return null;
};

const parseScanErrors = (errors: unknown): string[] => {
  if (!Array.isArray(errors)) {
    return [];
  }

  return errors
    .map((error) => {
      if (typeof error === 'string') {
        return error;
      }

      if (error && typeof error === 'object' && 'message' in error) {
        const message = (error as { message?: unknown }).message;

        return typeof message === 'string' ? message : '';
      }

      return '';
    })
    .filter(Boolean);
};

const parseInputCitations = (citations: unknown): PlagiarismInputCitation[] => {
  if (!Array.isArray(citations)) {
    return [];
  }

  return citations
    .map((citation) => {
      if (!citation || typeof citation !== 'object') {
        return null;
      }

      const item = citation as Record<string, unknown>;
      const status = normalizeCitationStatus(item.status);

      if (!status || !Array.isArray(item.span)) {
        return null;
      }

      return {
        span: item.span as TextSpan,
        status,
        citation_texts: Array.isArray(item.citation_texts)
          ? item.citation_texts.map((text) => String(text))
          : [],
        ...(typeof item.is_quoted === 'boolean'
          ? { is_quoted: item.is_quoted }
          : {}),
        ...(typeof item.is_cited === 'boolean'
          ? { is_cited: item.is_cited }
          : {}),
      } satisfies PlagiarismInputCitation;
    })
    .filter(
      (citation): citation is PlagiarismInputCitation => citation !== null,
    );
};

export const parseCitationAwareScanResult = (
  result: Partial<CitationAwareScanResult> & Record<string, unknown>,
): CitationAwareScanResult | null => {
  if (result.input_plagiarism_pct === undefined) {
    return null;
  }

  const inputCitations = parseInputCitations(result.input_citations);

  return sanitizeCitationAwareScanResult({
    language: String(result.language || 'en'),
    ...(result.document_pages !== undefined
      ? { document_pages: Number(result.document_pages) }
      : {}),
    input_plagiarism_pct: Number(result.input_plagiarism_pct || 0),
    input_exact_plagiarism_pct: Number(result.input_exact_plagiarism_pct || 0),
    input_minor_plagiarism_pct: Number(result.input_minor_plagiarism_pct || 0),
    web_input_plagiarism_pct: Number(result.web_input_plagiarism_pct || 0),
    vector_input_plagiarism_pct: Number(
      result.vector_input_plagiarism_pct || 0,
    ),
    num_words_total: Number(result.num_words_total || 0),
    input_exact_plagiarism_num_words: Number(
      result.input_exact_plagiarism_num_words || 0,
    ),
    input_minor_plagiarism_num_words: Number(
      result.input_minor_plagiarism_num_words || 0,
    ),
    properly_cited_quoted_num_words: Number(
      result.properly_cited_quoted_num_words || 0,
    ),
    omitted_num_words: Number(result.omitted_num_words || 0),
    sources_count: Number(result.sources_count || 0),
    web_sources_count: Number(result.web_sources_count || 0),
    vector_sources_count: Number(result.vector_sources_count || 0),
    contains_citations: Boolean(
      result.contains_citations ?? inputCitations.length > 0,
    ),
    engine_version: String(result.engine_version || ''),
    errors: parseScanErrors(result.errors),
    input_plagiarism: Array.isArray(result.input_plagiarism)
      ? result.input_plagiarism
      : [],
    input_exact_plagiarism: Array.isArray(result.input_exact_plagiarism)
      ? result.input_exact_plagiarism
      : [],
    input_minor_plagiarism: Array.isArray(result.input_minor_plagiarism)
      ? result.input_minor_plagiarism
      : [],
    references_span: Array.isArray(result.references_span)
      ? (result.references_span as TextSpan)
      : null,
    web_sources: Array.isArray(result.web_sources)
      ? (result.web_sources as PlagiarismWebSource[])
      : [],
    vector_sources: Array.isArray(result.vector_sources)
      ? (result.vector_sources as PlagiarismVectorSource[])
      : [],
    input_citations: inputCitations,
  });
};
